import type { AllowedIconName, Choice } from '@monthio/design-system'
import type { FunctionComponent } from 'react'

import {
	COLORS,
	FlowPage,
	IconSquare,
	MultipleChoice,
} from '@monthio/design-system'
import React from 'react'
import { useTranslation } from 'react-i18next'

import type { ExtraOrdinaryItemGroupType } from '../SavingsCapacity.types'

import { ExtraOrdinaryItemGroupTypes } from '../SavingsCapacity.types'

export const categoryToIconMap: Record<
	ExtraOrdinaryItemGroupType,
	AllowedIconName
> = {
	homeImprovement: 'Build',
	largeCelebrations: 'Diversity1',
	unusualTravels: 'FlightsAndHotels',
	largeLeisurelyPurchases: 'SportsTennis',
	otherExtraordinaryExpenses: 'ReceiptLong',
}

export const ExtraordinaryItemsGroupSelectorPage: FunctionComponent<{
	initialSelectedGroupTypes: ExtraOrdinaryItemGroupType[]
	onNext: (selectedGroupTypes: ExtraOrdinaryItemGroupType[]) => void
}> = ({ initialSelectedGroupTypes, onNext }) => {
	const { t } = useTranslation()
	const [selectedCategories, setSelectedCategories] = React.useState<
		ExtraOrdinaryItemGroupType[]
	>(initialSelectedGroupTypes)

	const hasExtraordinaryItems = selectedCategories.length > 0
	const continueButtonTranslationKey = hasExtraordinaryItems
		? 'savingsCapacity-extraordinaryItemsCategories-button-selected'
		: 'savingsCapacity-extraordinaryItemsCategories-button-notSelected'

	const categoryChoices: Choice[] = ExtraOrdinaryItemGroupTypes.map((key) => ({
		description: t(
			`savingsCapacity-extraordinaryItemsCategories-category-${key}-description`,
		),
		value: key,
		label: t(
			`savingsCapacity-extraordinaryItemsCategories-category-${key}-title`,
		),
		startDecorator: (
			<IconSquare
				icon={categoryToIconMap[key]}
				size="m"
				backgroundColor={COLORS.graphics.surface.nonSemantic.brightBlue}
				color={COLORS.graphics.surface.nonSemantic.brightBlueInverted}
			/>
		),
	}))

	return (
		<FlowPage>
			<FlowPage.Content>
				<FlowPage.Header
					title={t('savingsCapacity-extraordinaryItemsCategories-header-title')}
				/>
				<MultipleChoice
					size="m"
					defaultValue={selectedCategories}
					fieldName="extraordinaryItemsCategories"
					choices={categoryChoices}
					type="checkbox"
					onChange={(values) => {
						const selectedValues = values as ExtraOrdinaryItemGroupType[]
						const sortedValues = selectedValues.sort(
							(a, b) =>
								ExtraOrdinaryItemGroupTypes.indexOf(a) -
								ExtraOrdinaryItemGroupTypes.indexOf(b),
						)
						setSelectedCategories(sortedValues)
					}}
				/>
			</FlowPage.Content>
			<FlowPage.Footer
				actions={
					<FlowPage.Cta onClick={() => onNext(selectedCategories)}>
						{t(continueButtonTranslationKey)}
					</FlowPage.Cta>
				}
			/>
		</FlowPage>
	)
}

// savingsCapacity-extraordinaryItemsCategories-category-homeImprovement-description
// savingsCapacity-extraordinaryItemsCategories-category-homeImprovement-title
// savingsCapacity-extraordinaryItemsCategories-category-largeCelebrations-description
// savingsCapacity-extraordinaryItemsCategories-category-largeCelebrations-title
// savingsCapacity-extraordinaryItemsCategories-category-unusualTravels-description
// savingsCapacity-extraordinaryItemsCategories-category-unusualTravels-title
// savingsCapacity-extraordinaryItemsCategories-category-largeLeisurelyPurchases-description
// savingsCapacity-extraordinaryItemsCategories-category-largeLeisurelyPurchases-title
// savingsCapacity-extraordinaryItemsCategories-category-extraordinaryExpenses-description
// savingsCapacity-extraordinaryItemsCategories-category-extraordinaryExpenses-title
